// =============================================================================
// directorReportExport.ts — T-395
// Weekly directors' meeting pack: PDF (pdfmake, lazy-loaded like
// projectFinancialsExport) + Excel. Content: live company snapshot, updates by
// category, actions completed, open actions by assignee, overdue in red.
// =============================================================================

import * as XLSX from "xlsx";
// @ts-ignore — Vite ?url suffix isn't in pdfmake's type declarations
import pdfmakeScriptUrl from "pdfmake/build/pdfmake.min.js?url";
// @ts-ignore
import vfsFontsScriptUrl from "pdfmake/build/vfs_fonts.js?url";

export interface ReportEntry {
  kind:      string;
  category:  string;
  title:     string;
  details:   string | null;
  status:    string;
  priority:  string;
  assignee:  string;         // resolved name or ""
  due_date:  string | null;
  done_at:   string | null;
  created_at: string;
}

export interface CompanySnapshot {
  liveProjects:      number;
  liveContractValue: number;
  arOutstanding:     number;
  apOwing:           number;
  monthlyBurn:       number;
  complianceExpiring30d: number;
}

export interface DirectorReportInput {
  periodStart: string;   // yyyy-mm-dd
  periodEnd:   string;
  snapshot:    CompanySnapshot;
  newUpdates:  ReportEntry[];   // updates created in period
  completed:   ReportEntry[];   // actions done in period
  openActions: ReportEntry[];   // open/in_progress/parked at period end
}

const money = (v: number) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtD  = (d: string | null) => d ? new Date(d).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const CAT_LABEL: Record<string, string> = {
  company: "Company", finance: "Finance", projects: "Projects",
  compliance: "Compliance", people: "People",
};
const cat = (c: string) => CAT_LABEL[c] ?? c;

// --------------------------------------------------------------------- XLSX
export function exportDirectorReportXlsx(input: DirectorReportInput): void {
  const wb = XLSX.utils.book_new();
  const today = new Date().toISOString().slice(0, 10);

  const summary: unknown[][] = [
    ["Coretel Group — directors' report"],
    ["Period", `${fmtD(input.periodStart)} → ${fmtD(input.periodEnd)}`],
    ["Generated", new Date().toLocaleString("en-AU")],
    [],
    ["Company snapshot"],
    ["Live projects", input.snapshot.liveProjects],
    ["Live contract value", input.snapshot.liveContractValue],
    ["AR outstanding (inc GST)", input.snapshot.arOutstanding],
    ["AP owing (inc GST)", input.snapshot.apOwing],
    ["Monthly overhead burn (ex GST)", input.snapshot.monthlyBurn],
    ["Compliance items expiring ≤30d", input.snapshot.complianceExpiring30d],
  ];
  const ws0 = XLSX.utils.aoa_to_sheet(summary);
  ws0["!cols"] = [{ wch: 32 }, { wch: 28 }];
  XLSX.utils.book_append_sheet(wb, ws0, "Summary");

  const entrySheet = (name: string, rows: ReportEntry[], withStatus: boolean) => {
    const header = withStatus
      ? ["Category", "Title", "Details", "Assigned to", "Priority", "Status", "Due", "Done"]
      : ["Category", "Title", "Details", "Logged"];
    const aoa: unknown[][] = [header, ...rows.map(r => withStatus
      ? [cat(r.category), r.title, r.details ?? "", r.assignee, r.priority, r.status.replace(/_/g, " "), fmtD(r.due_date), fmtD(r.done_at)]
      : [cat(r.category), r.title, r.details ?? "", fmtD(r.created_at)])];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = header.map(h => ({ wch: h === "Details" ? 60 : h === "Title" ? 40 : 16 }));
    ws["!autofilter"] = { ref: `A1:${XLSX.utils.encode_col(header.length - 1)}${aoa.length}` };
    XLSX.utils.book_append_sheet(wb, ws, name);
  };

  entrySheet("Updates", input.newUpdates, false);
  entrySheet("Completed", input.completed, true);
  entrySheet("Open actions", input.openActions, true);

  XLSX.writeFile(wb, `directors_report_${today}.xlsx`);
}

// ---------------------------------------------------------------------- PDF
function loadScriptOnce(src: string, tag: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[data-pdfmake-tag="${tag}"]`);
    if (existing) {
      if (existing.dataset.loaded === "true") resolve();
      else { existing.addEventListener("load", () => resolve()); existing.addEventListener("error", () => reject(new Error(`reload ${tag}`))); }
      return;
    }
    const s = document.createElement("script");
    s.src = src; s.async = true; s.dataset.pdfmakeTag = tag;
    s.onload = () => { s.dataset.loaded = "true"; resolve(); };
    s.onerror = () => reject(new Error(`load ${tag}`));
    document.head.appendChild(s);
  });
}

async function getPdfMake(): Promise<any> {
  const existing = (window as any).pdfMake;
  if (existing?.vfs && Object.keys(existing.vfs).length > 0) return existing;
  await loadScriptOnce(pdfmakeScriptUrl, "pdfmake-core");
  await loadScriptOnce(vfsFontsScriptUrl, "pdfmake-vfs");
  const pdfMake = (window as any).pdfMake;
  if (!pdfMake) throw new Error("pdfMake missing");
  return pdfMake;
}

const C = { primary: "#ff7844", ink: "#111", muted: "#666", panel: "#f6f6f4", rule: "#e6e6e3", danger: "#b91c1c", ok: "#166534", warn: "#92400e" };

export async function exportDirectorReportPdf(input: DirectorReportInput): Promise<void> {
  const pdfMake = await getPdfMake();
  const today = new Date().toISOString().slice(0, 10);
  const todayIso = today;

  const kpiCell = (label: string, value: string) => ({
    stack: [
      { text: label, fontSize: 7.5, color: C.muted, margin: [0, 0, 0, 2] },
      { text: value, fontSize: 12, bold: true, color: C.ink },
    ],
    fillColor: C.panel,
    margin: [8, 6, 8, 6],
  });

  const sectionHead = (t: string) => ({
    text: t, fontSize: 11, bold: true, color: C.ink, margin: [0, 14, 0, 6],
  });

  const entryTable = (rows: ReportEntry[], withStatus: boolean) => {
    if (rows.length === 0) return { text: "Nothing this period.", fontSize: 9, italics: true, color: C.muted };
    const header = withStatus
      ? ["Category", "Item", "Assigned to", "Priority", "Due", "Status"]
      : ["Category", "Item", "Logged"];
    const body: any[][] = [header.map(h => ({ text: h, fontSize: 8, bold: true, color: "#fff", fillColor: "#3a3a3a" }))];
    for (const r of rows) {
      const overdue = withStatus && r.due_date && r.due_date < todayIso && r.status !== "done";
      const item = {
        stack: [
          { text: r.title, fontSize: 9, bold: true, color: C.ink },
          ...(r.details ? [{ text: r.details, fontSize: 8, color: C.muted, margin: [0, 1, 0, 0] }] : []),
        ],
      };
      body.push(withStatus
        ? [
            { text: cat(r.category), fontSize: 8.5 },
            item,
            { text: r.assignee || "—", fontSize: 8.5 },
            { text: r.priority, fontSize: 8.5, color: r.priority === "critical" || r.priority === "high" ? C.danger : C.muted },
            { text: fmtD(r.due_date), fontSize: 8.5, color: overdue ? C.danger : C.ink, bold: !!overdue },
            { text: r.status === "done" ? `done ${fmtD(r.done_at)}` : r.status.replace(/_/g, " "), fontSize: 8.5, color: r.status === "done" ? C.ok : overdue ? C.danger : C.warn },
          ]
        : [
            { text: cat(r.category), fontSize: 8.5 },
            item,
            { text: fmtD(r.created_at), fontSize: 8.5, color: C.muted },
          ]);
    }
    return {
      table: {
        headerRows: 1,
        widths: withStatus ? [58, "*", 70, 42, 52, 60] : [58, "*", 60],
        body,
      },
      layout: {
        hLineWidth: () => 0.5, vLineWidth: () => 0,
        hLineColor: () => C.rule,
        paddingTop: () => 4, paddingBottom: () => 4, paddingLeft: () => 4, paddingRight: () => 4,
      },
    };
  };

  const overdueOpen = input.openActions.filter(r => r.due_date && r.due_date < todayIso);

  const doc = {
    pageSize: "A4",
    pageMargins: [36, 40, 36, 44],
    footer: (page: number, pages: number) => ({
      columns: [
        { text: "Coretel Group — directors' report (director-only)", fontSize: 7, color: C.muted },
        { text: `${page} / ${pages}`, alignment: "right", fontSize: 7, color: C.muted },
      ],
      margin: [36, 12, 36, 0],
    }),
    content: [
      { text: "Directors' report", fontSize: 18, bold: true, color: C.ink },
      { text: `${fmtD(input.periodStart)} → ${fmtD(input.periodEnd)}   ·   generated ${new Date().toLocaleString("en-AU")}`, fontSize: 9, color: C.muted, margin: [0, 2, 0, 12] },

      // Snapshot KPIs
      {
        table: {
          widths: ["*", "*", "*"],
          body: [
            [
              kpiCell("LIVE PROJECTS", `${input.snapshot.liveProjects} · ${money(input.snapshot.liveContractValue)}`),
              kpiCell("AR OUTSTANDING", money(input.snapshot.arOutstanding)),
              kpiCell("AP OWING", money(input.snapshot.apOwing)),
            ],
            [
              kpiCell("MONTHLY BURN (EX GST)", money(input.snapshot.monthlyBurn)),
              kpiCell("COMPLIANCE EXPIRING ≤30D", String(input.snapshot.complianceExpiring30d)),
              kpiCell("OPEN ACTIONS", `${input.openActions.length}${overdueOpen.length ? ` (${overdueOpen.length} overdue)` : ""}`),
            ],
          ],
        },
        layout: { hLineWidth: () => 2, vLineWidth: () => 2, hLineColor: () => "#fff", vLineColor: () => "#fff" },
      },

      sectionHead(`Updates this period (${input.newUpdates.length})`),
      entryTable(input.newUpdates, false),

      sectionHead(`Actions completed (${input.completed.length})`),
      entryTable(input.completed, true),

      sectionHead(`Open actions (${input.openActions.length})`),
      entryTable(
        // overdue first, then by due date
        [...input.openActions].sort((a, b) =>
          (a.due_date ?? "9999").localeCompare(b.due_date ?? "9999")),
        true,
      ),
    ],
  };

  pdfMake.createPdf(doc).download(`directors_report_${today}.pdf`);
}
